import { OneleetRole, TenantRole } from "@oneleet/api_client";

import { colors } from "./colors";

export const DEFAULT_ANIMATION_DURATION_MILLISECONDS = 150;
export const MILLISECONDS_IN_A_SECOND = 1000;

export const EVENT_PROPAGATION_DELAY = 1000;

export const MOBILE_BREAKPOINT = 640;

export type AppId = "app" | "portal" | "trust" | "tester" | "admin";

export const fontWeights = {
  "ng-normal": "440",
  "ng-medium": "520",
  "ng-semibold": "640",
} as const;

type FontSize = [string, FontSizeOptions];

interface FontSizeOptions {
  lineHeight: string;
  letterSpacing: string;
  fontWeight: string | number;
}

type TextFontSize = [
  string,
  FontSizeOptions & { uppercaseLetterSpacing: string },
];

export const textFontSizes: Record<string, TextFontSize> = {
  "ng-xs": [
    // 10px
    "0.625rem",
    {
      // 12px
      lineHeight: ".75rem",
      // 0.1px
      letterSpacing: "0.00625rem",
      uppercaseLetterSpacing: "0.5px",
      fontWeight: fontWeights["ng-normal"],
    },
  ],
  "ng-sm": [
    // 12px
    "0.7500rem",
    {
      // 16px
      lineHeight: "1rem",
      // 0.01px
      letterSpacing: "0.000625rem",
      uppercaseLetterSpacing: "0.36px",
      fontWeight: fontWeights["ng-normal"],
    },
  ],
  "ng-md": [
    // 13px
    "0.8125rem",
    {
      // 20px
      lineHeight: "1.25rem",
      // -0.04px
      letterSpacing: "-0.0025rem",
      uppercaseLetterSpacing: "0.39px",
      fontWeight: fontWeights["ng-normal"],
    },
  ],
  "ng-base": [
    // 15px
    "0.9375rem",
    {
      // 20px
      lineHeight: "1.25rem",
      // -0.13px
      letterSpacing: "-0.008125rem",
      uppercaseLetterSpacing: "0.45px",
      fontWeight: fontWeights["ng-normal"],
    },
  ],
  "ng-lg": [
    // 17px
    "1.0625rem",
    {
      // 24px
      lineHeight: "1.5rem",
      // -0.22px
      letterSpacing: "-.01375rem",
      uppercaseLetterSpacing: "0.51px",
      fontWeight: fontWeights["ng-normal"],
    },
  ],
};

const headerFontSizes: Record<string, FontSize> = {
  "heading-xs": [
    // 13px
    "0.8125rem",
    {
      // 16px
      lineHeight: "1rem",
      // -0.04px
      letterSpacing: "-0.0025rem",
      fontWeight: fontWeights["ng-semibold"],
    },
  ],
  "heading-sm": [
    // 15px
    "0.9375rem",
    {
      // 20px
      lineHeight: "1.25rem",
      // -0.13px
      letterSpacing: "-0.008125rem",
      fontWeight: fontWeights["ng-semibold"],
    },
  ],
  "heading-md": [
    // 17px
    "1.0625rem",
    {
      // 20px
      lineHeight: "1.25rem",
      // -0.22px
      letterSpacing: "-.01375rem",
      fontWeight: fontWeights["ng-semibold"],
    },
  ],
  "heading-lg": [
    // 21px
    "1.3125rem",
    {
      // 24px
      lineHeight: "1.5rem",
      // -0.37px
      letterSpacing: "-.023125rem",
      fontWeight: fontWeights["ng-semibold"],
    },
  ],
  "heading-xl": [
    // 25px
    "1.5625rem",
    {
      // 28px
      lineHeight: "1.75rem",
      // -0.5px
      letterSpacing: "-.03125rem",
      fontWeight: fontWeights["ng-semibold"],
    },
  ],
  "heading-2xl": [
    // 29px
    "1.8125rem",
    {
      // 36px
      lineHeight: "2.25rem",
      // -0.61px
      letterSpacing: "-.038125rem",
      fontWeight: fontWeights["ng-semibold"],
    },
  ],
};

export const fontSizes: Record<string, FontSize> = {
  ...textFontSizes,
  ...headerFontSizes,
};

export const textUnderlinePositions = [
  "auto",
  "from-font",
  "under",
  "left",
  "right",
];

export { BRAND_COLOR, colors } from "./colors";

export const DEFAULT_ICON_SIZE = "size-4";
export const DEFAULT_ICON_COLOR = "text-neutral-550";

export const DEFAULT_TRUST_PAGE_HEADER_COLOR = colors.gray[900];
export const DEFAULT_TRUST_PAGE_ACCENT_COLOR = colors["ng-blue"][800];

export const ONELEET_ADMIN_ROLES = [OneleetRole.SUPERADMIN, OneleetRole.ADMIN];
export const ONELEET_DASHBOARD_ACCESS_TENANT_ROLES = [
  TenantRole.ADMIN,
  TenantRole.MEMBER,
];
